import classNames from "classnames";
import { BuilderComponentProps } from "../BuilderInterface";
import "./style.scss";
import { getCompoundStyle, getStateStyleForBuilder, getStylesFromInteractiveStates, limitCharacters } from "../helpers";
import { NodeStates, TextNode } from "../../build/types";
import { useEffect, useRef, useState } from "react";
import { useOutsideClick } from "../../hooks/useOutsideClick";
import { useInteractionStates } from "../../hooks/useInteractionStates";

const BuilderText: React.FC<BuilderComponentProps> = ({
    node,
    actionCallbacks,
    dynamicData,
    builderProps,
    disabled
}: BuilderComponentProps) => {
    const tNode = node as TextNode;
    const { id, attr, stateManager, text, prepend, append, characterLimit } = tNode;
    const { setElement, states } = useInteractionStates();
    const [editText, setEditText] = useState<boolean>(false);
    const [textAreaScroll, setTextAreaScroll] = useState<number>(0);

    const activateTextEdit = (e: React.MouseEvent) => {
        if (builderProps?.selectCallback && !tNode.dynamicData) {
            builderProps.selectCallback(e, node.id);
            if (inputRef.current) {
                setTextAreaScroll(inputRef.current.scrollHeight);
            }
            setEditText(true);
        }
    };

    const inputRef = useRef<HTMLTextAreaElement>(null);

    useOutsideClick(inputRef, () => {
        deactivateTextEdit();
    });

    const deactivateTextEdit = () => {
        setEditText(false);
    };

    const handleInput = (e: React.FormEvent<HTMLTextAreaElement>) => {
        if (builderProps?.inputCallback) {
            builderProps.inputCallback(e.currentTarget.value);
        }
    };

    const buildTextClass = classNames("builder-text", {
        [`${attr?.className}`]: !!attr?.className
    });

    useEffect(() => {
        if (inputRef.current) {
            setTextAreaScroll(inputRef.current.scrollHeight);
        }
    }, [inputRef.current?.value]);

    if (builderProps) {
        const { nodeState, selectedMenu, selectCallback, outlineCallback, onDragStart, onDragEnd, inputCallback } = builderProps;

        return editText && inputCallback ? (
            <textarea
                className={buildTextClass}
                style={getCompoundStyle({
                    node,
                    otherStyle: textAreaScroll > 0 ? { height: `${textAreaScroll}px` } : { height: "fit-content" },
                    stateStyle: getStateStyleForBuilder(stateManager, nodeState, "attr_style")
                })}
                id={id}
                value={text}
                onInput={handleInput}
                ref={inputRef}
                onClick={e => e.stopPropagation()}
            />
        ) : (
            <>
                {node.dynamicData !== undefined && !dynamicData ? null : (
                    <span
                        {...attr}
                        className={buildTextClass}
                        style={getCompoundStyle({
                            node,
                            stateStyle: getStateStyleForBuilder(stateManager, nodeState, "attr_style")
                        })}
                        onClick={e => (selectCallback ? selectCallback(e, id) : {})}
                        onMouseMove={e => (outlineCallback ? outlineCallback(e, id) : {})}
                        id={id}
                        onDragStart={e => onDragStart?.(e, id)}
                        draggable={"true"}
                        onDragEnd={onDragEnd}
                        onDoubleClick={activateTextEdit}
                    >
                        {prepend?.value && <span style={prepend.style}>{prepend.value}</span>}
                        {tNode.dynamicData ? limitCharacters(dynamicData || "", characterLimit, true) : text}
                        {append?.value && <span style={append.style}>{append.value}</span>}
                        {selectedMenu}
                    </span>
                )}
            </>
        );
    } else {
        const textStyle = getStylesFromInteractiveStates({
            states,
            defaultStyle: getCompoundStyle({ node }),
            stateManager,
            paths: ["attr_style"],
            ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
        });

        const prependStyle = getStylesFromInteractiveStates({
            states,
            stateManager,
            paths: ["prepend_style"],
            ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
        });

        const appendStyle = getStylesFromInteractiveStates({
            states,
            stateManager,
            paths: ["append_style"],
            ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
        });

        return (
            <>
                {node.dynamicData !== undefined && !dynamicData ? null : (
                    <span ref={setElement} {...attr} {...actionCallbacks} className={buildTextClass} style={textStyle}>
                        {prepend?.value && <span style={prependStyle}>{prepend.value}</span>}
                        {tNode.dynamicData ? limitCharacters(dynamicData || "", characterLimit, true) : text}
                        {append?.value && <span style={appendStyle}>{append.value}</span>}
                    </span>
                )}
            </>
        );
    }
};

export default BuilderText;
